import { useState, useCallback, useRef } from "react";
import {
  useInitiateMultipartUploadMutation,
  useGetUploadPartUrlMutation,
  useCompleteMultipartUploadMutation,
  useAbortMultipartUploadMutation,
} from "./Upload.api";

// Minimum 5MB chunk size (required for S3/R2 multipart except last part)
const MIN_CHUNK_SIZE = 5 * 1024 * 1024;
const MAX_PARTS = 10_000;

/**
 * Calculate chunk size based on file size to stay within the 10,000 part limit.
 * Uses 5MB minimum, but scales up for larger files.
 */
const getChunkSize = (fileSize: number): number => {
  return Math.max(MIN_CHUNK_SIZE, Math.ceil(fileSize / MAX_PARTS));
};

interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
  currentPart: number;
  totalParts: number;
  speed: number; // bytes per second
  eta: number;   // seconds remaining
}

interface UseMultipartUploadOptions {
  onProgress?: (progress: UploadProgress) => void;
  onSuccess?: (location?: string, contentId?: string) => void;
  onError?: (error: Error) => void;
}


export function useMultipartUpload(options: UseMultipartUploadOptions = {}) {
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const uploadRef = useRef<{ key: string; uploadId: string } | null>(null);

  const [initiateUpload] = useInitiateMultipartUploadMutation();
  const [getUploadPartUrl] = useGetUploadPartUrlMutation();
  const [completeUpload] = useCompleteMultipartUploadMutation();
  const [abortUploadMutation] = useAbortMultipartUploadMutation();

  const uploadFile = useCallback(
    async (file: File) => {
      setIsUploading(true);
      setError(null);
      setProgress(null);

      // Extract file metadata
      const extension = file.name.split(".").pop() || "";
      const filename = file.name;
      const mimeType = file.type;
      const size = file.size;

      try {
        // Step 1: Initiate multipart upload (backend generates the key)
        const initResult = await initiateUpload({
          contentType: file.type,
          filename,
          extension,
          mimeType,
          size,
        }).unwrap();

        const { uploadId, key, content } = initResult;
        const contentId = content?.id;
        uploadRef.current = { key, uploadId };
        const chunkSize = getChunkSize(file.size);
        const totalParts = Math.ceil(file.size / chunkSize);
        const uploadedParts: { PartNumber: number; ETag: string }[] = [];

        const startTime = Date.now();

        // Initialize progress to 0%
        setProgress({
          loaded: 0,
          total: file.size,
          percentage: 0,
          currentPart: 0,
          totalParts,
          speed: 0,
          eta: 0,
        });

        // Step 2: Upload each part
        for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
          const start = (partNumber - 1) * chunkSize;
          const end = Math.min(start + chunkSize, file.size);
          const chunk = file.slice(start, end);

          // Get presigned URL for this part
          const partUrlResult = await getUploadPartUrl({
            key,
            uploadId,
            partNumber,
          }).unwrap();

          // Upload chunk directly to R2
          const uploadResponse = await fetch(partUrlResult.signedUrl, {
            method: "PUT",
            body: chunk,
            headers: {
              "Content-Type": "application/octet-stream",
            },
          });
          if (!uploadResponse.ok) {
            throw new Error(`Failed to upload part ${partNumber}`);
          }

          // Get ETag from response headers
          const etag = uploadResponse.headers.get("ETag");
          if (!etag) {
            throw new Error(`No ETag returned for part ${partNumber}`);
          }

          uploadedParts.push({
            PartNumber: partNumber,
            ETag: etag.replace(/"/g, ""), // Remove quotes from ETag
          });

          // Update progress
          const loaded = Math.min(partNumber * chunkSize, file.size);
          const elapsed = (Date.now() - startTime) / 1000; // in seconds
          const speed = elapsed > 0 ? loaded / elapsed : 0;
          const remainingBytes = file.size - loaded;
          const eta = speed > 0 ? remainingBytes / speed : 0;

          const progressUpdate: UploadProgress = {
            loaded,
            total: file.size,
            percentage: Math.round((loaded / file.size) * 100),
            currentPart: partNumber,
            totalParts,
            speed,
            eta,
          };
          setProgress(progressUpdate);
          options.onProgress?.(progressUpdate);
        }

        // Step 3: Complete multipart upload
        const completeResult = await completeUpload({
          key,
          uploadId,
          parts: uploadedParts,
        }).unwrap();

        uploadRef.current = null;
        setIsUploading(false);
        options.onSuccess?.(completeResult.location, contentId);

        return completeResult;
      } catch (err: any) {
        const errorMsg =
          err?.data?.error ||
          err?.message ||
          (err instanceof Error ? err.message : "Upload failed");
        const error = new Error(errorMsg);
        setError(error);
        setIsUploading(false);
        options.onError?.(error);
        throw error;
      }
    },
    [initiateUpload, getUploadPartUrl, completeUpload, options],
  );

  const abortUpload = useCallback(async () => {
    if (uploadRef.current) {
      try {
        await abortUploadMutation(uploadRef.current).unwrap();
      } catch (err) {
        console.error("Failed to abort upload:", err);
      } finally {
        uploadRef.current = null;
        setIsUploading(false);
        setProgress(null);
      }
    }
  }, [abortUploadMutation]);

  const reset = useCallback(() => {
    setIsUploading(false);
    setProgress(null);
    setError(null);
    uploadRef.current = null;
  }, []);

  return {
    uploadFile,
    isUploading,
    progress,
    error,
    reset,
    abortUpload,
    uploadRef,
  };
}
